import {
  ContentfulArticleMagnetPageCsrQuery,
  ContentfulArticleMagnetPageCsrQueryVariables,
  ContentfulPathMagnetPageCsrQuery,
  ContentfulPathMagnetPageCsrQueryVariables
} from "@outschool/gql-frontend-generated";
import { useLocale } from "@outschool/localization";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useParams } from "react-router-dom";

import { MAGNET_PAGE_PATHS } from "./constants";
import { contentfulMagnetPageFragment } from "./magnetQueries";

// For the "articles" page, include both old and new content:
// - Old articles have no `path` set (null), so we match with `path_exists: false`
// - New articles explicitly set `path: "articles"`
// The combined OR condition ensures both types are included:
// OR: [{ path_exists: false }, { path: $path }]
const CONTENTFUL_MAGNET_ARTICLE_PAGE = gql`
  ${contentfulMagnetPageFragment}
  query ContentfulArticleMagnetPageCSR(
    $slug: String!
    $skipRating: Boolean!
    $locale: String!
    $withRecentReviews: Boolean!
  ) {
    contentfulMagnetPageCollection(
      limit: 1
      skip: 0
      where: {
        magnetPageSlug: $slug
        OR: [{ path_exists: false }, { path: "articles" }]
      }
      locale: $locale
    ) {
      items {
        ...contentfulMagnetPageFragmentCSR
      }
    }
  }
`;

const CONTENTFUL_PATH_MAGNET_PAGE_QUERY = gql`
  ${contentfulMagnetPageFragment}
  query ContentfulPathMagnetPageCSR(
    $slug: String!
    $skipRating: Boolean!
    $locale: String!
    $withRecentReviews: Boolean!
    $path: String!
    $gradeNameSlug: String
  ) {
    contentfulMagnetPageCollection(
      limit: 1
      skip: 0
      where: {
        magnetPageSlug: $slug
        path: $path
        gradeNameSlug: $gradeNameSlug
      }
      locale: $locale
    ) {
      items {
        ...contentfulMagnetPageFragmentCSR
      }
    }
  }
`;

export default function useMagnetPageContent({
  path
}: {
  path: MAGNET_PAGE_PATHS;
}) {
  const { slug, gradeNameSlug } = useParams<{
    slug: string;
    gradeNameSlug?: string;
  }>();

  const locale = useLocale();

  const query =
    path === MAGNET_PAGE_PATHS.ARTICLES
      ? CONTENTFUL_MAGNET_ARTICLE_PAGE
      : CONTENTFUL_PATH_MAGNET_PAGE_QUERY;

  const variables =
    path === MAGNET_PAGE_PATHS.ARTICLES
      ? { slug, skipRating: false, locale, withRecentReviews: false }
      : {
          slug,
          skipRating: false,
          locale,
          withRecentReviews: false,
          path,
          gradeNameSlug:
            path === MAGNET_PAGE_PATHS.HOMESCHOOL_CURRICULUM
              ? gradeNameSlug
              : null
        };

  return useQueryWithPreviousData<
    ContentfulArticleMagnetPageCsrQuery | ContentfulPathMagnetPageCsrQuery,
    | ContentfulArticleMagnetPageCsrQueryVariables
    | ContentfulPathMagnetPageCsrQueryVariables
  >(query, {
    variables,
    skip: !slug
  });
}
